/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-shadow */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import Autosuggest from 'react-autosuggest';
import { withTranslation } from 'react-i18next';
import { differenceBy, find, remove } from 'lodash';
import { CirclePlus } from '@saleshandy/icons';
import ProspectTagWithCancel from '../../atoms/prospect-tag/with-cancel';
import Checkbox from '../../atoms/checkbox';
import { OverlayTooltip, Placement } from '../../overlay';
import TruncatedTextWithAutoTooltip from '../../../../components/truncated-text-with-auto-tooltip';
import '../tag-autosuggest/tag-autosuggest.scss';
import { RootState } from '../../../../../store/root-reducer';
import { accessibleOnClick, executeOnRequestStatus } from '../../../../utils';

// Render the selected tags inline or in block mode.
export enum RenderMode {
  Inline = 'Inline',
  Block = 'Block',
}

export interface ContainerProps {
  style: React.CSSProperties;
}

/**
 * Styles for the react-autosuggest <Autosuggest />
 * https://www.npmjs.com/package/react-autosuggest#theme-prop
 */
export interface Styles {
  suggestionsList?: React.CSSProperties;
  suggestionsContainer?: React.CSSProperties;
  suggestionsContainerOpen?: React.CSSProperties;
  suggestionHighlighted?: React.CSSProperties;
}

const extractTagsFromProspects = (prospects) => {
  const tagsToProspects = {};
  const prospectsTags = [];

  prospects.forEach((prospect) => {
    const { tags } = prospect;
    if (tags && tags.length) {
      tags.forEach((tag) => {
        const { id } = tag;
        if (tagsToProspects[id]) {
          tagsToProspects[id].push(prospect.id);
        } else {
          tagsToProspects[id] = [prospect.id];
          prospectsTags.push(tag);
        }
      });
    }
  });

  return prospectsTags;
};

const defaultTheme: Styles = {
  suggestionsList: {
    margin: 0,
    padding: 0,
    listStyleType: 'none',
  },
  suggestionsContainer: {
    position: 'absolute',
    zIndex: 99999,
    left: '1.5rem',
    right: '1.5rem',
  },
  suggestionsContainerOpen: {
    display: 'block',
    border: '2px solid green',
    borderRadius: '4px',
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '14px',
    lineHeight: '20px',
    marginTop: '10px',
    height: '11.45rem',
    overflow: 'auto',
    zIndex: 2,
    backgroundColor: 'white',
  },
  suggestionHighlighted: {
    backgroundColor: '#EFF6FF',
  },
};

type IProps = {
  selectedProspects?: any[];
  /**
   * Tags to display as suggestions.
   */
  tags: any[];
  /**
   * Tags which are needed to be removed.
   */
  tagsToRemove?: number[];
  /**
   * Tags which are tp be added to the prospects.
   */
  tagsToAdd: any[];
  setTagsToRemove?: (tags: any[]) => void;
  setTagsToAdd: (tags: any[]) => void;
  t: (x: string) => React.ReactNode;
  /**
   * Mode for rendering the selected tags
   */
  renderMode?: RenderMode;
  containerProps?: ContainerProps;
  styles?: Styles;

  showAddedTags?: boolean;
  showCreateSuggestion?: boolean;
  defaultSuggestionOpened?: boolean;
  restrictExistingOnly?: boolean;
  placeholder?: string;
  showCheckboxSuggestions?: boolean;
  maxLimit?: number | null;
  limitExceededRef: React.MutableRefObject<boolean>;
  /** Tag IDs that should be disabled (e.g. default lists) - shown but not selectable. Only applied when disableDefaultTagsBehavior is true */
  disabledTagIds?: number[];
  /** Tooltip text for disabled tags. Only applied when disableDefaultTagsBehavior is true */
  disabledTagTooltip?: string;
  /** When true, applies disabled state and tooltip for tags in disabledTagIds. When false, disabledTagIds/disabledTagTooltip are ignored */
  disableDefaultTagsBehavior?: boolean;
};

const TagAutoSuggest2: React.FC<IProps> = ({
  renderMode = RenderMode.Block,
  tags,
  selectedProspects = [],
  tagsToRemove = [],
  tagsToAdd,
  setTagsToAdd,
  setTagsToRemove,
  containerProps = {
    style: {},
  },
  t,
  styles = {
    suggestionsList: {
      height: '11rem',
    },
  },

  showAddedTags = false,
  showCreateSuggestion = false,
  defaultSuggestionOpened = false,
  restrictExistingOnly = false,
  placeholder = undefined,
  showCheckboxSuggestions = false,
  maxLimit = null,
  limitExceededRef,
  disabledTagIds = [],
  disabledTagTooltip = 'Default created list',
  disableDefaultTagsBehavior = false,
}) => {
  const inputRef = useRef(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const getTagListRequestStatus = useSelector(
    (state: RootState) => state.emailAccount.getEmailAccountTagsRequest.status,
  );

  const theme = useMemo(
    () => ({
      suggestionsList: {
        ...defaultTheme.suggestionsList,
        ...styles.suggestionsList,
      },
      suggestionsContainer: {
        ...defaultTheme.suggestionsContainer,
        ...styles.suggestionsContainer,
      },
      suggestionsContainerOpen: {
        ...defaultTheme.suggestionsContainerOpen,
        ...styles.suggestionsContainerOpen,
      },
      suggestionHighlighted: {
        ...defaultTheme.suggestionHighlighted,
        ...styles.suggestionHighlighted,
      },
    }),
    [],
  );

  const [inputValue, setInputValue] = useState<string>('');

  // Suggestions to display in autosuggest.
  const [suggestions, setSuggestions] = useState([]);

  // Tags which are already added/assigned/linked to the selected prospects.
  const [alreadyAddedTags, setAlreadyAddedTags] = useState([]);

  // Show Add Remove Tag Warning For Bulk Actions.
  const [showAddRemoveWarning, setShowAddRemoveWarning] = useState<boolean>(
    false,
  );

  const [forceSuggestions, setForceSuggestions] = useState(false);

  // Add Remove Tag Mode
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isAddingMode, setIsAddingMode] = useState(false);

  const [
    addRemoveTagWarningText,
    setAddRemoveTagWarningText,
  ] = useState<string>('');

  const updateLimitState = (currentCount: number, maxLimit: number | null) => {
    if (maxLimit !== null && currentCount > maxLimit) {
      limitExceededRef.current = true;
      setAddRemoveTagWarningText(
        `You can ${
          showCreateSuggestion ? 'add' : 'remove'
        } a maximum of ${maxLimit} tags.`,
      );
      setShowAddRemoveWarning(true);
    } else {
      limitExceededRef.current = false;
      setShowAddRemoveWarning(false);
    }
  };

  useEffect(() => {
    // Extracting tags which are already added/assigned/linked to selected prospects
    if (showAddedTags) {
      setAlreadyAddedTags(extractTagsFromProspects(selectedProspects));
    }

    // Refocus the input to keep suggestions open
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
      setSuggestions(getSuggestions(''));
    }, 1);
  }, []);

  useEffect(() => {
    executeOnRequestStatus({
      status: getTagListRequestStatus,
      onSuccess: () => {
        // Refocus the input to keep suggestions open
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
          setSuggestions(getSuggestions(inputValue));
        }, 1);
      },
    });
  }, [getTagListRequestStatus]);

  // useEffect(() => {
  //   if (isAddingMode) {
  //     // Refocus the input to keep suggestions open
  //     setTimeout(() => {
  //       if (inputRef.current) {
  //         inputRef.current.focus();
  //       }
  //       setSuggestions(getSuggestions(inputValue));
  //     }, 1);
  //   }
  // }, [tagsToAdd, inputValue]);

  useEffect(() => {
    if (defaultSuggestionOpened) {
      setSuggestions(getSuggestions(''));
    }
  }, [defaultSuggestionOpened, tags]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [tagsToAdd]);

  const hideAutoSuggestBorder = () => {
    theme.suggestionsContainerOpen.border = 'none';
  };

  const showAutoSuggestBorder = () => {
    theme.suggestionsContainerOpen.border = '1px solid #D1D5DB';
  };

  /**
   * Returns the valid list of suggestions to display.
   * How the validate which suggestions to display?
   *  1. The suggestion should include the input text.
   *  1. Its not already selected.
   *  2. If the exact match is not found then its a new tag.
   *  3. If no match is found then its a new tag
   * @param inputValue String: Input text
   */
  const getSuggestions = (inputValue: string) => {
    // Exact match found for the input value and a suggestion
    let exactMatch = false;

    // In checkbox multi-select mode we keep already selected tags in the list,
    // so the user can toggle selection via checkboxes.
    // When disableDefaultTagsBehavior is active, exclude disabled tags from the
    // dropdown entirely so they are never shown (instead of showing as disabled).
    const baseTags =
      showCheckboxSuggestions && disableDefaultTagsBehavior
        ? tags.filter((tag) => !disabledTagIds.includes(Number(tag.id)))
        : [...tags];
    const selectableSuggestions = showCheckboxSuggestions
      ? baseTags
      : differenceBy(baseTags, tagsToAdd.concat(alreadyAddedTags), 'name');

    const suggestions = selectableSuggestions.filter((tag) => {
      if (tag.name === inputValue) {
        exactMatch = true;
      }
      return tag.name.toLowerCase().includes(inputValue?.toLowerCase());
    });

    if (inputValue.length >= 3 && !suggestions.length) {
      /**
       * No Suggestions are found for this input value.
       * So user should be able to create a tag for this value.
       * Hiding the border because of design constraint :(
       */
      hideAutoSuggestBorder();
      return [
        {
          name: inputValue,
          isNew: true,
        },
      ];
    }

    showAutoSuggestBorder();

    if (inputValue.length >= 3 && !exactMatch) {
      suggestions.push({
        name: inputValue,
        isNew: true,
      });
    }

    return suggestions;
  };

  const getSuggestionValue = (suggestion) => suggestion.name;

  const disabledTagIdsSet = useMemo(
    () =>
      disableDefaultTagsBehavior ? new Set(disabledTagIds) : new Set<number>(),
    [disableDefaultTagsBehavior, disabledTagIds],
  );

  const onSuggestionSelected = (e: Event, { suggestion }) => {
    setIsAddingMode(true);

    if (
      disableDefaultTagsBehavior &&
      suggestion?.id != null &&
      disabledTagIdsSet.has(Number(suggestion.id))
    ) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (!showCheckboxSuggestions) {
      if (!suggestion.name.trim() || suggestion.name.trim().length < 3) {
        e.preventDefault();
        setAddRemoveTagWarningText('Tag must be at least 3 characters long.');
        setShowAddRemoveWarning(true);
        return;
      }
    }

    const existingTag = tags.find(
      (t) => t.name.trim() === suggestion.name.trim(),
    );
    const suggestionRef: any = {
      isNew: existingTag ? false : suggestion.isNew,
      name: suggestion.name.trim(),
    };

    if (existingTag) {
      suggestionRef.id = existingTag?.id ?? undefined;
    }

    if (!showCheckboxSuggestions && suggestion.name.trim().length >= 64) {
      e.preventDefault();
      return;
    }

    if (restrictExistingOnly && !existingTag) {
      if (!showCheckboxSuggestions) {
        setAddRemoveTagWarningText('Tag does not exist');
      }
      setShowAddRemoveWarning(true);
      e.preventDefault();
      return;
    }

    const isAlreadySelected =
      tagsToAdd
        .concat(alreadyAddedTags)
        .filter((s) => s.name === suggestionRef.name).length > 0;

    if (showCheckboxSuggestions && isAlreadySelected) {
      // Toggle off in checkbox multi-select mode
      const updatedTags = tagsToAdd.filter((t) => {
        if (suggestionRef?.id) return t?.id !== suggestionRef.id;
        return t?.name !== suggestionRef.name;
      });
      updateLimitState(updatedTags.length, maxLimit);
      setTagsToAdd(updatedTags);
      setInputValue('');
      return;
    }

    if (!isAlreadySelected) {
      const updatedTags = [...tagsToAdd, suggestionRef];
      updateLimitState(updatedTags.length, maxLimit);
      setTagsToAdd(updatedTags);
      setInputValue('');
    }
  };

  const onSuggestionsFetchRequest = ({ value }) => {
    setSuggestions(getSuggestions(value.trim()));
  };

  const onChange = (event, { newValue }) => {
    if (!showCheckboxSuggestions) {
      if (newValue.length > 64 && !showAddedTags) {
        setAddRemoveTagWarningText(
          'Tag name must not be greater than 64 characters.',
        );
        setShowAddRemoveWarning(true);
        return;
      }
    }
    updateLimitState(tagsToAdd.length, maxLimit);
    setInputValue(newValue);
    setShowAddRemoveWarning(false);
  };

  const onKeyDown = (event) => {
    if (
      event.keyCode === 13 &&
      event.key.trim().toLowerCase() === 'enter' &&
      inputValue.trim().length >= 3
    ) {
      onSuggestionSelected(event, {
        suggestion: {
          name: inputValue,
          isNew: true,
        },
      });
    } else if (event.keyCode === 13 && inputValue.trim().length < 3) {
      if (!showCheckboxSuggestions) {
        setAddRemoveTagWarningText('Tag must be at least 3 characters long.');
        setShowAddRemoveWarning(true);
      }
    }
  };

  const handleInputFocus = () => {
    setForceSuggestions(true); // Force suggestions to show on input focus
    setSuggestions(getSuggestions(inputValue));
  };

  const handleInputClick = () => {
    setForceSuggestions(true); // Ensure suggestions are shown on input click
    setSuggestions(getSuggestions(inputValue));
  };

  const renderSuggestion = (suggestion, showCreateSuggestion) => {
    if (suggestion.isNew) {
      // Suggest a new tag if it is in add tag mode
      if (showCreateSuggestion) {
        return (
          <div className="add-new-tag bs-py-6 bs-px-12">
            <CirclePlus color="#1D4ED8" /> Add "{inputValue}"
          </div>
        );
      }
      return null;
    }

    if (showCheckboxSuggestions) {
      const isDisabled =
        disableDefaultTagsBehavior &&
        suggestion?.id != null &&
        disabledTagIdsSet.has(Number(suggestion.id));
      const checked = tagsToAdd.some((t) => {
        if (suggestion?.id) return t?.id === suggestion.id;
        return t?.name === suggestion.name;
      });
      const content = (
        <div
          role="button"
          tabIndex={isDisabled ? -1 : 0}
          className={`d-flex align-items-center ${isDisabled ? '' : 'pointer'}`}
          style={{
            padding: '8px 24px',
            gap: '8px',
            opacity: isDisabled ? 0.5 : 1,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
          }}
          onClick={
            isDisabled
              ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }
              : undefined
          }
          onKeyDown={(e) => {
            if (isDisabled) return;

            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.currentTarget.click();
            }
          }}
        >
          <Checkbox checked={checked} disabled={isDisabled} />
          <TruncatedTextWithAutoTooltip
            text={suggestion.name}
            placement={Placement.Top}
          />
        </div>
      );
      return isDisabled && disabledTagTooltip ? (
        <OverlayTooltip text={disabledTagTooltip} placement={Placement.Top}>
          {content}
        </OverlayTooltip>
      ) : (
        content
      );
    }

    return <div className="bs-py-6 bs-px-12 pointer">{suggestion.name}</div>;
  };

  /**
   * Cancelling a tag can mean 2 things.
   *  1. It was a local tag -> it was not yet added to prospects.
   *  2. The tag was assigned to prospects. By cancelling here, we mean to remove this tag from the selected prospects
   */
  const cancelTag = (tag) => {
    setIsAddingMode(false);

    // this tag was already added to prospects;
    if (find(alreadyAddedTags, { id: tag.id })) {
      /**
       * This tag was already added to prospects.
       * So removing the tag from `alreadyAdded`.
       * And this tags needs to be removed from the selected prospects. So pushing it into the `tagsToRemove`
       */
      remove(alreadyAddedTags, { id: tag.id });
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      setTagsToRemove && setTagsToRemove([...tagsToRemove, tag.id]);
      setAlreadyAddedTags(alreadyAddedTags);
    } else {
      if (tag.isNew) {
        remove(tagsToAdd, { name: tag.name });
      } else {
        remove(tagsToAdd, { id: tag.id });
      }
      setTagsToAdd([...tagsToAdd]);
      updateLimitState(tagsToAdd.length, maxLimit);
    }
  };

  const renderSelectedTags = () =>
    alreadyAddedTags.concat(tagsToAdd).map((tag, idx) => (
      // eslint-disable-next-line react/no-array-index-key
      <div
        key={idx}
        className={`${
          showCheckboxSuggestions ? 'selected-tags-container' : ''
        }`}
      >
        <ProspectTagWithCancel
          name={tag.name}
          tooltipText={tag.name}
          onCancel={() => {
            cancelTag(tag);
          }}
        />
      </div>
    ));

  const renderInputComponent = (inputProps) => {
    if (renderMode === RenderMode.Inline) {
      return (
        <div ref={containerRef} className="fixed-height-input-container">
          <div className="tags-wrapper">
            {renderSelectedTags()}
            <div className="d-inline-block w-100">
              <div>
                <input
                  {...inputProps}
                  className="selected-tags-inline-input"
                  disabled={showAddedTags}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }
    return <input {...inputProps} className="bs-input" />;
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const inputProps = {
    placeholder:
      placeholder ??
      (showAddedTags
        ? t('messages.tags_removeMode')
        : t('messages.tags_placeholder')),
    value: inputValue,
    onChange,
    onKeyDown,
    onFocus: handleInputFocus,
    onClick: handleInputClick, // Add onClick handler
    ref: inputRef,
  };

  return (
    <div className="tags-autosuggest-container">
      <Autosuggest
        suggestions={[...suggestions]}
        onSuggestionsFetchRequested={onSuggestionsFetchRequest}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={(suggestion) =>
          renderSuggestion(suggestion, showCreateSuggestion)
        }
        inputProps={inputProps}
        renderInputComponent={renderInputComponent}
        theme={theme}
        onSuggestionSelected={onSuggestionSelected}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        // shouldRenderSuggestions={(value) => value.trim().length <= 64}
        shouldRenderSuggestions={(value) =>
          forceSuggestions || value.trim().length <= 64
        }
        containerProps={containerProps}
      />
      {showAddRemoveWarning && (
        <div
          className="error-text"
          style={{
            color: '#B91C1C',
            fontSize: '12px',
            marginTop: '0.25rem',
          }}
        >
          {addRemoveTagWarningText}
        </div>
      )}
    </div>
  );
};

export default withTranslation()(TagAutoSuggest2);
